import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, query, where, onSnapshot, addDoc, doc, deleteDoc, updateDoc
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  IoTrendingUpOutline, IoAddOutline, IoTrashOutline, IoPeopleOutline, IoCalendarOutline, IoRibbonOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoArrowBackOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './HomeScreen.css';

function AdminMetasScreen() {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSaaS = location.pathname.includes('/superuser/hr');
  const activeCondominioId = isSaaS ? 'SAAS' : userData?.condominioId;

  const [empleados, setEmpleados] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [metas, setMetas] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [metodoMedicion, setMetodoMedicion] = useState('Porcentaje');
  const [metaEsperada, setMetaEsperada] = useState('');
  const [bono, setBono] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch employees
  useEffect(() => {
    if (!activeCondominioId) return;

    const q = query(
      collection(db, "empleados"),
      where("condominioId", "==", activeCondominioId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmpleados(list);
      if (list.length > 0 && !selectedEmp) {
        setSelectedEmp(list[0]);
      }
    });
    return () => unsubscribe();
  }, [activeCondominioId]);

  // Fetch goals for selected employee
  useEffect(() => {
    if (!selectedEmp?.usuarioId || !activeCondominioId) {
      setMetas([]);
      return;
    }

    const q = query(
      collection(db, "trabajadores_metas"),
      where("condominioId", "==", activeCondominioId),
      where("trabajadorId", "==", selectedEmp.usuarioId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setMetas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [selectedEmp, activeCondominioId]);

  const handleAddMeta = async (e) => {
    e.preventDefault();
    if (!nombre || !selectedEmp) return;

    setLoading(true);
    try {
      await addDoc(collection(db, "trabajadores_metas"), {
        nombre,
        descripcion,
        metodoMedicion,
        metaEsperada,
        bono: Number(bono) || 0,
        fechaInicio,
        fechaFin,
        trabajadorId: selectedEmp.usuarioId,
        trabajadorNombre: `${selectedEmp.nombre} ${selectedEmp.apellidos}`,
        condominioId: activeCondominioId,
        estado: 'activo',
        createdAt: new Date()
      });
      setShowModal(false);
      setNombre('');
      setDescripcion('');
      setMetodoMedicion('Porcentaje');
      setMetaEsperada('');
      setBono('');
      setFechaInicio('');
      setFechaFin('');
    } catch (error) {
      console.error(error);
      alert("Error al agregar la meta.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEstado = async (metaId, nuevoEstado) => {
    if (!window.confirm(`¿Seguro que deseas marcar esta meta como "${nuevoEstado}"?`)) return;
    try {
      await updateDoc(doc(db, "trabajadores_metas", metaId), { estado: nuevoEstado });
    } catch (error) {
      console.error(error);
      alert("Error al actualizar la meta.");
    }
  };

  const handleDeleteMeta = async (metaId) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta meta?")) return;
    try {
      await deleteDoc(doc(db, "trabajadores_metas", metaId));
    } catch (error) {
      console.error(error);
      alert("Error al eliminar la meta.");
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/nominas-dashboard" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <style>{`
            .ks-responsive-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
            }
            @media (min-width: 768px) {
              .ks-responsive-grid {
                grid-template-columns: 280px 1fr;
                gap: 30px;
              }
            }
          `}</style>

          {/* Header Hero */}
          <div className="muro-header-stitch" style={{ marginBottom: '20px' }}>
            <div className="muro-hero-card" style={{ 
              background: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '15px 20px',
              minHeight: 'auto',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '15px', zIndex: 2 }}>
                <button 
                  onClick={() => navigate(isSaaS ? '/superuser/hr/dashboard' : '/nominas-dashboard')} 
                  className="usuarios-back-btn ks-back-btn-reco glass" 
                  title="Volver" 
                  style={{ margin: 0, position: 'relative', flexShrink: 0 }}
                >
                  <IoArrowBackOutline size={24} />
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                  <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.2, color: 'white', whiteSpace: 'normal', wordBreak: 'break-word' }}>Objetivos y Metas del Personal</h1>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)', whiteSpace: 'normal', wordBreak: 'break-word' }}>Asigna objetivos medibles, plazos y bonos para motivar e incentivar al equipo</p>
                </div>
              </div>
              <IoTrendingUpOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <div className="ks-responsive-grid">
              
              {/* Sidebar: Empleados */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: 'fit-content' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b' }}>
                  <IoPeopleOutline size={18} />
                  <span>Personal Operativo</span>
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {empleados.map(emp => {
                    const active = selectedEmp?.id === emp.id;
                    return (
                      <button
                        key={emp.id}
                        onClick={() => setSelectedEmp(emp)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px',
                          borderRadius: '12px',
                          border: active ? '1px solid #7e22ce' : '1px solid #f1f5f9',
                          background: active ? '#faf5ff' : 'white',
                          color: active ? '#7e22ce' : '#475569',
                          fontWeight: active ? '700' : '500',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}
                      >
                        <span style={{ fontSize: '0.9rem' }}>{emp.nombre} {emp.apellidos}</span>
                        <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{emp.puesto}</span>
                      </button>
                    );
                  })}
                  {empleados.length === 0 && <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No hay empleados registrados.</p>}
                </div>
              </div>

              {/* Main Area: Metas */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                {selectedEmp ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', color: '#1e293b' }}>
                          Metas y Bonos de {selectedEmp.nombre}
                        </h3>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>{selectedEmp.puesto}</p>
                      </div>
                      <button 
                        onClick={() => setShowModal(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: '#7e22ce',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        <IoAddOutline size={18} />
                        <span>Nueva Meta</span>
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {metas.map(meta => (
                        <div 
                          key={meta.id} 
                          style={{
                            padding: '16px',
                            borderRadius: '12px',
                            border: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '20px',
                                background: meta.estado === 'cumplido' ? '#dcfce7' : (meta.estado === 'no_cumplido' ? '#fee2e2' : '#fef3c7'),
                                color: meta.estado === 'cumplido' ? '#15803d' : (meta.estado === 'no_cumplido' ? '#b91c1c' : '#d97706'),
                                fontSize: '0.7rem',
                                fontWeight: '700',
                                textTransform: 'capitalize'
                              }}>{meta.estado === 'activo' ? 'En Curso' : meta.estado}</span>
                              <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <IoCalendarOutline size={12} /> {meta.fechaInicio} al {meta.fechaFin}
                              </span>
                            </div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', color: '#1e293b' }}>{meta.nombre}</h4>
                            <p style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b' }}>{meta.descripcion}</p>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: '#475569' }}>
                              <span><strong>Métrica:</strong> {meta.metaEsperada} ({meta.metodoMedicion})</span>
                              {meta.bono > 0 && (
                                <span style={{ color: '#15803d', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <IoRibbonOutline size={14} /> <strong>Bono:</strong> ${meta.bono}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {meta.estado === 'activo' && (
                              <>
                                <button 
                                  onClick={() => handleUpdateEstado(meta.id, 'cumplido')}
                                  style={{ background: '#dcfce7', border: 'none', color: '#15803d', borderRadius: '8px', padding: '6px', cursor: 'pointer' }}
                                  title="Marcar como cumplido"
                                >
                                  <IoCheckmarkCircleOutline size={18} />
                                </button>
                                <button 
                                  onClick={() => handleUpdateEstado(meta.id, 'no_cumplido')}
                                  style={{ background: '#fee2e2', border: 'none', color: '#b91c1c', borderRadius: '8px', padding: '6px', cursor: 'pointer' }}
                                  title="Marcar como no cumplido"
                                >
                                  <IoCloseCircleOutline size={18} />
                                </button>
                              </>
                            )}
                            <button 
                              onClick={() => handleDeleteMeta(meta.id)}
                              style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '8px' }}
                            >
                              <IoTrashOutline size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                      {metas.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                          <IoTrendingUpOutline size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                          <p style={{ margin: 0, fontSize: '0.9rem' }}>No hay metas asignadas a este empleado.</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p>Selecciona un empleado para gestionar sus objetivos.</p>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Modal: Agregar Meta */}
        {showModal && (
          <div className="notifications-overlay" onClick={() => setShowModal(false)}>
            <div className="notifications-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
              <div className="notifications-modal-header">
                <h2>Nueva Meta / Objetivo</h2>
                <button className="close-modal-btn" onClick={() => setShowModal(false)}>X</button>
              </div>
              <form onSubmit={handleAddMeta} style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Nombre del Objetivo *</label>
                  <input 
                    type="text" 
                    value={nombre} 
                    onChange={e => setNombre(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                    placeholder="Ej. Mantener 98% Asistencia"
                    required 
                  />
                </div>
                
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Criterios de Evaluación / Descripción</label>
                  <textarea 
                    value={descripcion} 
                    onChange={e => setDescripcion(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', minHeight: '60px' }} 
                    placeholder="Escribe cómo se evaluará esta meta"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Método de Medición</label>
                    <select 
                      value={metodoMedicion} 
                      onChange={e => setMetodoMedicion(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                    >
                      <option value="Porcentaje">Porcentaje (%)</option>
                      <option value="Número / Cantidad">Número / Cantidad</option>
                      <option value="Completado / No completado">Sí / No (Booleano)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Meta Esperada *</label>
                    <input 
                      type="text" 
                      value={metaEsperada} 
                      onChange={e => setMetaEsperada(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                      placeholder="Ej. 95% o 10 tareas"
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Fecha Inicio</label>
                    <input 
                      type="date" 
                      value={fechaInicio} 
                      onChange={e => setFechaInicio(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                      required
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Fecha Fin / Vencimiento</label>
                    <input 
                      type="date" 
                      value={fechaFin} 
                      onChange={e => setFechaFin(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                      required
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Bono Extra por Cumplimiento ($ MXN)</label>
                  <input 
                    type="number" 
                    value={bono} 
                    onChange={e => setBono(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                    placeholder="Ej. 500"
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    background: '#7e22ce',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                >
                  {loading ? 'Guardando...' : 'Asignar Meta'}
                </button>
              </form>
            </div>
          </div>
        )}

      </main>
      <KondoBottomNav activeRoute="/nominas-dashboard" />
    </div>
  );
}

export default AdminMetasScreen;
